/**
 * Every React Query key in one place, so invalidation can't drift from the
 * query that uses it. Keys are arrays; invalidating a prefix (e.g.
 * queryKeys.employees.all) refreshes everything under it.
 */
export const queryKeys = {
  companies: {
    all: ["companies"],
    descendants: () => ["companies", "descendants"],
    hierarchy: (companyId) => ["companies", "hierarchy", companyId],
  },
  branches: {
    all: ["branches"],
    byCompany: (companyId) => ["branches", "company", companyId],
    hierarchy: (branchId) => ["branches", "hierarchy", branchId],
  },
  departments: {
    all: ["departments"],
    list: (companyId) => ["departments", companyId || "all"],
  },
  roles: { all: ["roles"] },
  jobTitles: { all: ["jobTitles"] },
  clients: { all: ["clients"] },

  employees: {
    all: ["employees"],
    list: (filters) => ["employees", "list", filters],
    count: (filters) => ["employees", "count", filters],
    detail: (id) => ["employees", "detail", id],
    image: (id) => ["employees", "image", id],
  },

  // Background employee import: progress + results of one batch
  employeeImport: {
    batch: (batchId) => ["employeeImport", batchId],
  },

  // Employee profile sections, all scoped to one employee
  profile: {
    all: (employeeId) => ["profile", employeeId],
    personal: (employeeId) => ["profile", employeeId, "personal"],
    addressCurrent: (employeeId) => [
      "profile",
      employeeId,
      "address",
      "current",
    ],
    addressPermanent: (employeeId) => [
      "profile",
      employeeId,
      "address",
      "permanent",
    ],
    contacts: (employeeId) => ["profile", employeeId, "contacts"],
    history: (employeeId) => ["profile", employeeId, "history"],
    education: (employeeId) => ["profile", employeeId, "education"],
    documents: (employeeId) => ["profile", employeeId, "documents"],
  },

  leave: {
    all: ["leave"],
    companyRequestCount: (params) => ["leave", "companyRequestCount", params],
    policies: () => ["leave", "policies"],
    types: () => ["leaveTypes"],
    balances: (employeeId) => ["employeeLeaveBalances", employeeId],
    list: (employeeId) => ["leaveList", employeeId],
  },

  users: { all: ["users"] },
  auditLogs: { all: ["auditLogs"], list: (params) => ["auditLogs", params] },
  importLogs: {
    all: ["importLogs"],
    history: (params) => ["importLogs", "history", params],
    batch: (batchId) => ["importLogs", "batch", batchId],
  },
  storageConfigs: {
    all: ["storageConfigs"],
    list: (scope) => ["storageConfigs", "list", scope],
    template: (provider) => ["storageConfigs", "template", provider],
  },
  syncConfigs: { all: ["syncConfigs"] },
};

// Dropdown data (companies, branches, roles...) rarely changes.
export const LOOKUP_STALE_TIME = 5 * 60 * 1000;
